// Client-side musical KEY + TEMPO (BPM) detection. Pure DSP over the decoded
// PCM we already hold for separation — no server, no dependencies, no extra
// download. Runs in well under a second on a downsampled mono mixdown.
//
//   • Key — chromagram (FFT → 12 pitch classes, octave-folded) correlated
//     against the Krumhansl–Kessler major/minor key profiles (24 candidates).
//   • BPM — energy-flux onset envelope → autocorrelation, with octave
//     correction so we don't report half/double tempo.
//
// Both are ESTIMATES. Key is ambiguous between relative major/minor (C major
// vs A minor share notes); BPM struggles on rubato/beatless material. The UI
// surfaces a confidence so low-certainty results can be flagged with a "~".

export interface KeyBpmResult {
  keyName: string;            // "C major" / "A minor"
  keyShort: string;           // "C" / "Am"
  tonic: string;              // "C"
  mode: 'major' | 'minor';
  altKeyShort: string | null; // relative key, when major/minor is too close to call
  keyConfidence: number;      // 0..1
  bpm: number;                // rounded
  bpmConfidence: number;      // 0..1
}

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

// Krumhansl–Kessler tonal hierarchy profiles (major / natural minor).
const MAJOR_PROFILE = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
const MINOR_PROFILE = [6.33, 2.68, 3.52, 5.38, 2.6, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

function downmixMono(left: Float32Array, right: Float32Array): Float32Array {
  const n = Math.min(left.length, right.length);
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) out[i] = (left[i] + right[i]) * 0.5;
  return out;
}

// Box-average decimation — crude anti-alias, plenty for chroma + onset envelope.
function decimate(x: Float32Array, factor: number): Float32Array {
  if (factor <= 1) return x.slice(0);
  const n = Math.floor(x.length / factor);
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    let s = 0;
    const off = i * factor;
    for (let j = 0; j < factor; j++) s += x[off + j];
    out[i] = s / factor;
  }
  return out;
}

function hann(n: number): Float32Array {
  const w = new Float32Array(n);
  for (let i = 0; i < n; i++) w[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (n - 1)));
  return w;
}

// In-place iterative radix-2 FFT (n must be a power of two).
function fft(re: Float32Array, im: Float32Array): void {
  const n = re.length;
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      const tr = re[i]; re[i] = re[j]; re[j] = tr;
      const ti = im[i]; im[i] = im[j]; im[j] = ti;
    }
  }
  for (let len = 2; len <= n; len <<= 1) {
    const ang = (-2 * Math.PI) / len;
    const wpr = Math.cos(ang);
    const wpi = Math.sin(ang);
    const half = len >> 1;
    for (let i = 0; i < n; i += len) {
      let wr = 1, wi = 0;
      for (let k = 0; k < half; k++) {
        const a = i + k;
        const b = a + half;
        const tr = re[b] * wr - im[b] * wi;
        const ti = re[b] * wi + im[b] * wr;
        re[b] = re[a] - tr; im[b] = im[a] - ti;
        re[a] += tr; im[a] += ti;
        const nwr = wr * wpr - wi * wpi;
        wi = wr * wpi + wi * wpr;
        wr = nwr;
      }
    }
  }
}

function pearson(a: ArrayLike<number>, b: ArrayLike<number>): number {
  const n = a.length;
  let ma = 0, mb = 0;
  for (let i = 0; i < n; i++) { ma += a[i]; mb += b[i]; }
  ma /= n; mb /= n;
  let num = 0, da = 0, db = 0;
  for (let i = 0; i < n; i++) {
    const x = a[i] - ma;
    const y = b[i] - mb;
    num += x * y; da += x * x; db += y * y;
  }
  const den = Math.sqrt(da * db);
  return den === 0 ? 0 : num / den;
}

function detectKey(mono: Float32Array, sr: number) {
  const N = 8192;
  const hop = 4096;
  const win = hann(N);
  const re = new Float32Array(N);
  const im = new Float32Array(N);
  const chroma = new Float64Array(12);
  const minF = 55;    // ~A1
  const maxF = 2000;  // fundamentals; higher partials fold in via octave-wrap
  const minBin = Math.max(1, Math.floor((minF * N) / sr));
  const maxBin = Math.min(N >> 1, Math.ceil((maxF * N) / sr));

  for (let start = 0; start + N <= mono.length; start += hop) {
    for (let i = 0; i < N; i++) { re[i] = mono[start + i] * win[i]; im[i] = 0; }
    fft(re, im);
    for (let bin = minBin; bin < maxBin; bin++) {
      const mag = Math.hypot(re[bin], im[bin]);
      if (mag <= 0) continue;
      const f = (bin * sr) / N;
      const midi = Math.round(69 + 12 * Math.log2(f / 440));
      let pc = midi % 12;
      if (pc < 0) pc += 12;
      chroma[pc] += mag;
    }
  }

  let sum = 0;
  for (let i = 0; i < 12; i++) sum += chroma[i];
  if (sum <= 0) return null; // silence

  let best = { corr: -Infinity, tonic: 0, mode: 'major' as 'major' | 'minor' };
  let second = -Infinity;
  const rc = new Float64Array(12);
  for (let t = 0; t < 12; t++) {
    for (let i = 0; i < 12; i++) rc[i] = chroma[(t + i) % 12];
    const cMaj = pearson(rc, MAJOR_PROFILE);
    const cMin = pearson(rc, MINOR_PROFILE);
    for (const [corr, mode] of [[cMaj, 'major'], [cMin, 'minor']] as const) {
      if (corr > best.corr) { second = best.corr; best = { corr, tonic: t, mode }; }
      else if (corr > second) second = corr;
    }
  }

  // Relative key (C major ↔ A minor): if it scores nearly as well, flag both.
  const relTonic = best.mode === 'major' ? (best.tonic + 9) % 12 : (best.tonic + 3) % 12;
  const relMode = best.mode === 'major' ? 'minor' : 'major';
  for (let i = 0; i < 12; i++) rc[i] = chroma[(relTonic + i) % 12];
  const relCorr = pearson(rc, relMode === 'major' ? MAJOR_PROFILE : MINOR_PROFILE);

  const tonicName = NOTE_NAMES[best.tonic];
  const keyShort = best.mode === 'major' ? tonicName : `${tonicName}m`;
  const relShort = relMode === 'major' ? NOTE_NAMES[relTonic] : `${NOTE_NAMES[relTonic]}m`;
  const ambiguous = best.corr - relCorr < 0.04;

  return {
    keyName: `${tonicName} ${best.mode}`,
    keyShort,
    tonic: tonicName,
    mode: best.mode,
    altKeyShort: ambiguous ? relShort : null,
    keyConfidence: clamp01((best.corr - 0.4) / 0.5),
  };
}

function detectBpm(mono: Float32Array, sr: number) {
  const frameLen = 512;
  const hop = 128;
  const nFrames = Math.floor((mono.length - frameLen) / hop);
  if (nFrames < 16) return { bpm: 0, bpmConfidence: 0 };
  const envRate = sr / hop; // onset-envelope samples per second

  const energy = new Float64Array(nFrames);
  for (let f = 0; f < nFrames; f++) {
    let s = 0;
    const off = f * hop;
    for (let i = 0; i < frameLen; i++) { const v = mono[off + i]; s += v * v; }
    energy[f] = Math.log(1 + s);
  }
  // Onset strength = half-wave-rectified first difference, mean-subtracted.
  const flux = new Float64Array(nFrames);
  let mean = 0;
  for (let f = 1; f < nFrames; f++) { const d = energy[f] - energy[f - 1]; const v = d > 0 ? d : 0; flux[f] = v; mean += v; }
  mean /= nFrames;
  for (let f = 0; f < nFrames; f++) { flux[f] -= mean; if (flux[f] < 0) flux[f] = 0; }

  const minBpm = 50, maxBpm = 210;
  const minLag = Math.max(1, Math.floor((60 * envRate) / maxBpm));
  const maxLag = Math.min(nFrames - 1, Math.ceil((60 * envRate) / minBpm));
  const acf = new Float64Array(maxLag + 1);
  let bestLag = minLag, bestVal = -Infinity, acfMean = 0;
  for (let lag = minLag; lag <= maxLag; lag++) {
    let s = 0;
    for (let f = lag; f < nFrames; f++) s += flux[f] * flux[f - lag];
    acf[lag] = s;
    acfMean += s;
    if (s > bestVal) { bestVal = s; bestLag = lag; }
  }
  acfMean /= (maxLag - minLag + 1);

  let bpm = (60 * envRate) / bestLag;
  // Octave correction: among ½×, 1×, 2× keep the candidate with the strongest
  // ACF, mildly favouring the common 90–150 range.
  let bestScore = -Infinity, chosen = bpm;
  for (const c of [bpm / 2, bpm, bpm * 2]) {
    if (c < 60 || c > 180) continue;
    const lag = Math.round((60 * envRate) / c);
    const score = (lag >= 0 && lag < acf.length ? acf[lag] : 0) * (c >= 90 && c <= 150 ? 1.15 : 1);
    if (score > bestScore) { bestScore = score; chosen = c; }
  }

  return {
    bpm: Math.round(chosen),
    bpmConfidence: clamp01((bestVal / (acfMean + 1e-9) - 1) / 3),
  };
}

/**
 * Detect musical key + BPM from decoded stereo PCM. Resolves to null when the
 * clip is too short or silent to analyse. Yields once before the heavy DSP so
 * the caller's UI can paint first.
 */
export async function detectKeyBpm(
  left: Float32Array,
  right: Float32Array,
  sampleRate: number,
): Promise<KeyBpmResult | null> {
  await new Promise((r) => setTimeout(r, 0));

  const mono0 = downmixMono(left, right);
  const totalSec = mono0.length / sampleRate;
  if (totalSec < 3) return null;

  // Analyse a central ≤120 s segment (skip a short intro), then decimate to
  // ~11 kHz — enough for chroma + tempo, ~4× less work.
  const skip = Math.floor(Math.min(5, totalSec * 0.05) * sampleRate);
  const maxLen = Math.floor(120 * sampleRate);
  const seg = mono0.subarray(skip, Math.min(mono0.length, skip + maxLen));
  const factor = Math.max(1, Math.round(sampleRate / 11025));
  const sr = sampleRate / factor;
  const mono = decimate(seg, factor);

  const key = detectKey(mono, sr);
  const tempo = detectBpm(mono, sr);
  if (!key) return null;

  return { ...key, ...tempo };
}
